/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect, ChangeEvent, useMemo, useRef } from "react";
import {
  Form,
  Table,
  Modal,
  Button,
  Spinner,
  Badge,
  Offcanvas,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { Bounce, toast } from "react-toastify";
import { getReportName, toTimestamp } from "../utilities/functions";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface ReportData {
  id_report: number;
  turn_report: number;
  date_creation: string;
  id_user: number;
  nom_user: string;
  prenom_user: string;
  date_debut: string;
  date_fin: string;
  PSN: string | null;
  immatriculation_vehicule: string | null;
  state_report: number;
  type_report: string;
  id_dispositif: number;
}

interface FleetReportsPersistedState {
  currentPage: number;
  limit: number;
  sortDirection: "ASC" | "DESC";
  sortType: string;
  searchTerm: string;
  searchOption: string;
  dateFrom: string;
  dateTo: string;
}

export function FleetReports() {
  const { translate } = useTranslate();
  const user_id = localStorage.getItem("userid") || "0";
  const STORAGE_KEY = `fleetReportsState:${user_id}`;

  const readPersistedState = (): FleetReportsPersistedState => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return {
          currentPage: 1,
          limit: 10,
          sortDirection: "DESC",
          sortType: "turn_report",
          searchTerm: "",
          searchOption: "vehicule",
          dateFrom: "",
          dateTo: "",
        };
      }

      const parsed = JSON.parse(raw);

      return {
        currentPage:
          Number(parsed.currentPage) > 0 ? Number(parsed.currentPage) : 1,
        limit: Number(parsed.limit) > 0 ? Number(parsed.limit) : 10,
        sortDirection: parsed.sortDirection === "ASC" ? "ASC" : "DESC",
        sortType: parsed.sortType || "turn_report",
        searchTerm: parsed.searchTerm || "",
        searchOption: parsed.searchOption || "vehicule",
        dateFrom: parsed.dateFrom || "",
        dateTo: parsed.dateTo || "",
      };
    } catch {
      return {
        currentPage: 1,
        limit: 10,
        sortDirection: "DESC",
        sortType: "turn_report",
        searchTerm: "",
        searchOption: "vehicule",
        dateFrom: "",
        dateTo: "",
      };
    }
  };

  const persisted = readPersistedState();

  const [listReports, setListReports] = useState<ReportData[]>([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [limit, setLimit] = useState<number>(persisted.limit);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState<number>(persisted.currentPage);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">(persisted.sortDirection);
  const [sortType, setSortType] = useState<string>(persisted.sortType);

  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedReportsData, setSelectedReportsData] = useState<ReportData[]>([]);
  const [selectedItems, setSelectedItems] = useState<ReportData[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState<string>(persisted.searchTerm);
  const [searchOption, setSearchOption] = useState<string>(persisted.searchOption);
  const [dateFrom, setDateFrom] = useState<string>(persisted.dateFrom);
  const [dateTo, setDateTo] = useState<string>(persisted.dateTo);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const isFirstRenderRef = useRef(true);

  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        currentPage,
        limit,
        sortDirection,
        sortType,
        searchTerm,
        searchOption,
        dateFrom,
        dateTo,
        statusFilter
      })
    );
  }, [
    STORAGE_KEY,
    currentPage,
    limit,
    sortDirection,
    sortType,
    searchTerm,
    searchOption,
    dateFrom,
    dateTo,
    statusFilter
  ]);

  const placeholderText = useMemo(() => {
    let text = translate("Search by ");

    switch (searchOption) {
      case "vehicule":
        text += translate("Vehicle");
        break;
      case "user":
        text += translate("User");
        break;
      case "turn":
        text += translate("Turn Report");
        break;
      default:
        text += "...";
    }

    return text;
  }, [searchOption, translate]);

  const fetchReportData = async (
    page: number,
    pageLimit: number,
    column: string,
    order: string,
    term: string | null,
    option: string | null,
    from: string | null,
    to: string | null,
    status: string | null
  ) => {
    try {
      setLoading(true);

      const totalParams = new URLSearchParams();
      if (term) totalParams.append("searchTerm", term);
      if (option) totalParams.append("searchOption", option);
      if (from) totalParams.append("dateFrom", from);
      if (to) totalParams.append("dateTo", to);
   

      const totalRes = await fetch(
        `${backendUrl}/api/rapport/Fleetreport/totalpage/${user_id}?${totalParams.toString()}`,
        { mode: "cors" }
      );

      if (!totalRes.ok) {
        throw new Error(`Erreur total fleet reports: ${totalRes.status}`);
      }

      const totalData = await totalRes.json();
      const totalValue =
        Array.isArray(totalData) && totalData[0]
          ? Number(totalData[0].total)
          : 0;

      setTotal(totalValue);
      setPageCount(Math.ceil(totalValue / pageLimit));

      const listParams = new URLSearchParams({
        page: String(page),
        limit: String(pageLimit),
        sortColumn: column,
        sortOrder: order,
      });

      if (term) listParams.append("searchTerm", term);
      if (option) listParams.append("searchOption", option);
      if (from) listParams.append("dateFrom", from);
      if (to) listParams.append("dateTo", to);
      if (status) totalParams.append("status", status);
      if (status) listParams.append("status", status);

      const res = await fetch(
        `${backendUrl}/api/rapports/Fleetreport/${user_id}?${listParams.toString()}`,
        { mode: "cors" }
      );

      if (!res.ok) {
        throw new Error(
          `Erreur lors de la récupération des rapports. Statut : ${res.status}`
        );
      }

      const data = await res.json();
      const safeData = Array.isArray(data) ? data : [];

      setListReports(safeData);
      setSelectedItems((prev) =>
        prev.filter((selected) =>
          safeData.some((row) => row.id_report === selected.id_report)
        )
      );
    } catch (error) {
      console.error(error);
      setListReports([]);
      setTotal(0);
      setPageCount(0);
      setSelectedItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchReportData(
        currentPage,
        limit,
        sortType,
        sortDirection,
        searchTerm,
        searchOption,
        dateFrom,
        dateTo,
        statusFilter
      );
    }, 250);

    return () => clearTimeout(timeout);
  }, [currentPage, limit, sortType, sortDirection, searchTerm, searchOption, dateFrom, dateTo,statusFilter]);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    setCurrentPage(1);
    setSelectAll(false);
    setSelectedItems([]);
  }, [searchTerm, searchOption, dateFrom, dateTo]);

  const handlePageClick = (data: { selected: number }) => {
    setCurrentPage(data.selected + 1);
  };

  const handleSelectChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newValue = parseInt(event.target.value, 10);
    setLimit(newValue);
    setCurrentPage(1);
  };

  const handleSort = (type: string) => {
    let sortOrder: "ASC" | "DESC" = "ASC";

    if (type === sortType) {
      sortOrder = sortDirection === "ASC" ? "DESC" : "ASC";
    }

    setSortType(type);
    setSortDirection(sortOrder);
    setCurrentPage(1);
  };

  const handleSelectAll = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const isChecked = target.checked;
    setSelectAll(isChecked);

    if (isChecked) {
      setSelectedItems(listReports);
    } else {
      setSelectedItems([]);
    }
  };

  const handleCheckboxChange = (
    { target }: ChangeEvent<HTMLInputElement>,
    report: ReportData
  ) => {
    const isChecked = target.checked;

    setSelectedItems((prevSelectedItems) => {
      if (isChecked) {
        const exists = prevSelectedItems.some(
          (selectedItem) => selectedItem.id_report === report.id_report
        );
        return exists ? prevSelectedItems : [...prevSelectedItems, report];
      }

      return prevSelectedItems.filter(
        (selectedItem) => selectedItem.id_report !== report.id_report
      );
    });
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (error) {
      console.error("Clipboard copy error:", error);
    }
  };

  const showTypeReportList = async (
    turn_report: number,
    id_dispositif: number
  ) => {
    try {
      const res = await fetch(
        `${backendUrl}/api/reports/byTurn/${turn_report}/${id_dispositif}`,
        { mode: "cors" }
      );

      if (!res.ok) {
        throw new Error(
          `Error retrieving reports for turn. Status : ${res.status}`
        );
      }

      const data = await res.json();
      setSelectedReportsData(Array.isArray(data) ? data : []);
      setShowDrawer(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleItemClick = (report: ReportData) => {
    const url = `/report/${report.id_report}/${report.turn_report}/${report.type_report}/${report.id_dispositif}`;
    window.location.href = url;
  };

  const supprimerRapportsSelectionnes = async () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmation = async () => {
    setShowConfirmationModal(false);

    try {
      setDeleteLoading(true);

      const uniqueTurns = Array.from(
        new Map(
          selectedItems.map((item) => [
            item.turn_report,
            { turn_report: item.turn_report },
          ])
        ).values()
      );

      for (const selectedReport of uniqueTurns) {
        const response = await fetch(`${backendUrl}/api/fleet-reports/delete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(selectedReport),
        });

        if (!response.ok) {
          throw new Error(
            `Erreur lors de la suppression des rapports. Statut : ${response.status}`
          );
        }
      }

      toast.success(translate("Reports Deleted successfully !"), {
        position: "bottom-right",
        autoClose: 2400,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });

      setSelectedItems([]);
      setSelectAll(false);

      await fetchReportData(
        currentPage,
        limit,
        sortType,
        sortDirection,
        searchTerm,
        searchOption,
        dateFrom,
        dateTo,
        statusFilter
      );
    } catch (error) {
      console.error(
        "Erreur lors de la suppression des rapports sélectionnés :",
        error
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
  };

  const clearSearchTerm = () => {
    setSearchTerm("");
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSearchOption("vehicule");
    setDateFrom("");
    setDateTo("");
    setSortType("turn_report");
    setSortDirection("DESC");
    setLimit(10);
    setCurrentPage(1);
    setSelectAll(false);
    setSelectedItems([]);
    setStatusFilter("");
  };

  const checkAndUpdateReportStatus = async () => {
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(limit),
        sortColumn: sortType,
        sortOrder: sortDirection,
      });

      if (searchTerm) params.append("searchTerm", searchTerm);
      if (searchOption) params.append("searchOption", searchOption);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);

      const res = await fetch(
        `${backendUrl}/api/rapports/Fleetreport/${user_id}?${params.toString()}`,
        { mode: "cors" }
      );

      if (!res.ok) {
        throw new Error(`Error while checking reports : ${res.status}`);
      }

      const newData = await res.json();
      const safeData = Array.isArray(newData) ? newData : [];

      const reportsWithChangedStatus = getDifferentReportIds(
        listReports,
        safeData
      );

      if (reportsWithChangedStatus.length > 0) {
        notify(reportsWithChangedStatus);
      }

      setListReports(safeData);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const hasProcessingReports = listReports.some(
      (report) => report.state_report === 2
    );

    if (!hasProcessingReports) return;

    const intervalId = setInterval(() => {
      checkAndUpdateReportStatus();
    }, 4000);

    return () => clearInterval(intervalId);
  }, [
    currentPage,
    limit,
    sortType,
    sortDirection,
    searchTerm,
    searchOption,
    dateFrom,
    dateTo,
    user_id,
    listReports,
  ]);

  const getDifferentReportIds = (list1: ReportData[], list2: ReportData[]) => {
    const differentIds: number[] = [];
    const minLength = Math.min(list1.length, list2.length);

    for (let i = 0; i < minLength; i++) {
      const report1 = list1[i];
      const report2 = list2[i];

      if (report1.state_report !== report2.state_report) {
        differentIds.push(report1.id_report);
      }
    }

    return differentIds;
  };

  const notify = (id_reports: number[]) => {
    toast.success(
      translate(
        `Reports with ids ${id_reports.toString()} calculet successfully !`
      ),
      {
        position: "bottom-right",
        autoClose: 2400,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      }
    );
  };

  const renderModernStateCell = (item: ReportData) => {
    let bg = "#fef3c7";
    let color = "#92400e";
    let label = translate("pending");
    let iconClass = "las la-clock";

    if (item.state_report === 1) {
      bg = "#dcfce7";
      color = "#166534";
      label = translate("calculated");
      iconClass = "las la-check-circle";
    } else if (item.state_report === 2) {
      bg = "#dbeafe";
      color = "#1d4ed8";
      label = translate("processing");
      iconClass = "las la-spinner la-spin";
    } else if (item.state_report === 0) {
      bg = "#fee2e2";
      color = "#b91c1c";
      label = translate("empty");
      iconClass = "las la-times-circle";
    }

    return (
      <button
        type="button"
        onClick={() => showTypeReportList(item.turn_report, item.id_dispositif)}
        style={{
          border: "none",
          background: bg,
          color,
          padding: "6px 12px",
          borderRadius: "999px",
          fontSize: "12px",
          fontWeight: 600,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i className={iconClass}></i>
        {label}
      </button>
    );
  };

  return (
    <>
      <div className="row">
        <div className="col-sm-12 col-md-6">
          <h4
            className="mb-3"
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <i
              className="ri ri-bar-chart-line"
              data-rel="bootstrap-tooltip"
              title="Rapports"
              style={{ color: "#f59e0b", fontSize: "31px" }}
            ></i>
            <span>
              {translate("Fleet report")} ({total})
            </span>
          </h4>
        </div>

        <div
          className="col-sm-12 col-md-6"
          style={{
            textAlign: "right",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <Link
            to="/new-fleet-report"
            className="btn"
            style={{
              background: "#000",
              border: "1px solid #000",
              color: "#fff",
              borderRadius: "10px",
              height: "44px",
              padding: "0 18px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              textDecoration: "none",
            }}
          >
            <i className="las la-plus"></i>
            {translate("new_fleet_report")}
          </Link>

          <Button
            className="btn"
            onClick={supprimerRapportsSelectionnes}
            disabled={selectedItems.length === 0 || deleteLoading}
            style={{
              background: "#f59e0b",
              border: "1px solid #f59e0b",
              color: "#fff",
              borderRadius: "10px",
              height: "44px",
              padding: "0 18px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" />
                {translate("Deleting")}
              </>
            ) : (
              <>
                <i className="las la-trash"></i>
                {translate("Delete")}
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 16,
              boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
              marginBottom: 16,
            }}
          >
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <Form.Label>{translate("search")}</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type="text"
                    placeholder={placeholderText}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button variant="light" onClick={clearSearchTerm}>
                      <i className="las la-times-circle"></i>
                    </Button>
                  )}
                </div>
              </div>

              <div className="col-md-1">
                <Form.Label>{translate("Search by")}</Form.Label>
                <Form.Select
                  value={searchOption}
                  onChange={(e) => setSearchOption(e.target.value)}
                >
                  <option value="vehicule">{translate("Vehicle")}</option>
                  <option value="user">{translate("User")}</option>
                  <option value="turn">{translate("turn")}</option>
                </Form.Select>
              </div>

              <div className="col-md-2">
                <Form.Label>{translate("date_from")}</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <Form.Label>{translate("date_to")}</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>


              <div className="col-md-1">
                <Form.Label>{translate("State")}</Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">{translate("All")}</option>
                  <option value="1">{translate("calculated")}</option>
                  <option value="2">{translate("processing")}</option>
                  <option value="0">{translate("empty")}</option>
                </Form.Select>
              </div>

              <div className="col-md-1">
                <Form.Label>{translate("Show")}</Form.Label>
                <Form.Select value={limit} onChange={handleSelectChange}>
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                </Form.Select>
              </div>

                          <div className="col-md-2 d-grid">
                <Button
                  variant="outline-secondary"
                  onClick={resetFilters}
                >
                  {translate("Reset")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
            }}
          >
            <Table hover responsive className="mb-0">
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th style={{ width: 45 }}>
                    <div className="checkbox d-inline-block">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        id="checkbox1"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <label htmlFor="checkbox1" className="mb-0"></label>
                    </div>
                  </th>

                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("turn_report")}
                  >
                    {translate("turn")}
                    {sortType === "turn_report" && sortDirection === "ASC" && " ▲"}
                    {sortType === "turn_report" && sortDirection === "DESC" && " ▼"}
                  </th>

                  <th>{translate("Type")}</th>
                  <th>{translate("Vehicle")}</th>
                  <th>{translate("user_name")}</th>

                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("date_creation")}
                  >
                    {translate("Creation date")}
                    {sortType === "date_creation" && sortDirection === "ASC" && " ▲"}
                    {sortType === "date_creation" && sortDirection === "DESC" && " ▼"}
                  </th>

                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("date_debut")}
                  >
                    {translate("Start date")}
                    {sortType === "date_debut" && sortDirection === "ASC" && " ▲"}
                    {sortType === "date_debut" && sortDirection === "DESC" && " ▼"}
                  </th>

                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("date_fin")}
                  >
                    {translate("End date")}
                    {sortType === "date_fin" && sortDirection === "ASC" && " ▲"}
                    {sortType === "date_fin" && sortDirection === "DESC" && " ▼"}
                  </th>

                  <th>{translate("State")}</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                      </div>
                    </td>
                  </tr>
                ) : listReports.length > 0 ? (
                  listReports.map((item) => (
                    <tr key={item.id_report}>
                      <td>
                        <div className="checkbox d-inline-block">
                          <input
                            type="checkbox"
                            className="checkbox-input"
                            id={`checkbox-${item.id_report}`}
                            checked={selectedItems.some(
                              (selectedItem) =>
                                selectedItem.id_report === item.id_report
                            )}
                            onChange={(event) =>
                              handleCheckboxChange(event, item)
                            }
                          />
                          <label
                            htmlFor={`checkbox-${item.id_report}`}
                            className="mb-0"
                          ></label>
                        </div>
                      </td>

                      <td
                        style={{
                          cursor: "pointer",
                          position: "relative",
                          padding: "10px",
                        }}
                        title={translate("click_to_copy_turn")}
                        onClick={() =>
                          copyToClipboard(
                            item.turn_report.toString(),
                            `turn-${item.id_report}`
                          )
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              color:
                                copiedId === `turn-${item.id_report}`
                                  ? "#22c55e"
                                  : "#0f172a",
                            }}
                          >
                            {item.turn_report}
                          </span>
                          <i
                            className="las la-copy"
                            style={{
                              fontSize: "14px",
                              color:
                                copiedId === `turn-${item.id_report}`
                                  ? "#22c55e"
                                  : "#94a3b8",
                            }}
                          ></i>
                        </div>

                        {copiedId === `turn-${item.id_report}` && (
                          <span
                            style={{
                              position: "absolute",
                              top: "-24px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              background: "#22c55e",
                              color: "#fff",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                            }}
                          >
                            ✔ {translate("turn_copied")}
                          </span>
                        )}
                      </td>

                      <td>
                        <Badge bg="light" text="dark" pill>
                          [{item.type_report}]{" "}
                          {getReportName(item.type_report, translate)}
                        </Badge>
                      </td>

                      <td
                        style={{
                          cursor: item.immatriculation_vehicule
                            ? "pointer"
                            : "default",
                          position: "relative",
                          padding: "10px",
                        }}
                        title={translate("click_to_copy")}
                        onClick={() => {
                          if (item.immatriculation_vehicule) {
                            copyToClipboard(
                              item.immatriculation_vehicule,
                              `vehicule-${item.id_report}`
                            );
                          }
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 500,
                              color:
                                copiedId === `vehicule-${item.id_report}`
                                  ? "#22c55e"
                                  : "#0f172a",
                            }}
                          >
                            {item.immatriculation_vehicule || "-"}
                          </span>
                          <i
                            className="las la-copy"
                            style={{
                              fontSize: "14px",
                              color:
                                copiedId === `vehicule-${item.id_report}`
                                  ? "#22c55e"
                                  : "#94a3b8",
                            }}
                          ></i>
                        </div>

                        {copiedId === `vehicule-${item.id_report}` && (
                          <span
                            style={{
                              position: "absolute",
                              top: "-24px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              background: "#22c55e",
                              color: "#fff",
                              padding: "4px 10px",
                              borderRadius: "6px",
                              fontSize: "11px",
                              boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                            }}
                          >
                            ✔ {translate("copied")}
                          </span>
                        )}
                      </td>

                      <td>
                        {`${item.nom_user || ""} ${item.prenom_user || ""}`.trim() || "-"}
                      </td>
                      <td>{toTimestamp(item.date_creation)}</td>
                      <td>{toTimestamp(item.date_debut)}</td>
                      <td>{toTimestamp(item.date_fin)}</td>
                      <td>{renderModernStateCell(item)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      {translate("No report available")}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        <div className="col-md-6 d-flex align-items-center mt-3">
          <span>
            {translate("Displaying")} {listReports.length} {translate("out of")} {total}
          </span>
        </div>

        <div className="col-md-6 d-flex justify-content-end mt-3">
          <ReactPaginate
            previousLabel={"previous"}
            nextLabel={"next"}
            breakLabel={"..."}
            pageCount={pageCount}
            forcePage={Math.max(currentPage - 1, 0)}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-right"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
          />
        </div>
      </div>

      <Offcanvas
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        placement="end"
        style={{ width: 460 }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{translate("Report List")}</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {selectedReportsData && selectedReportsData.length > 0 ? (
            <div>
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: 14,
                  padding: 16,
                  marginBottom: 16,
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <strong>{translate("Turn Report")}:</strong>{" "}
                  {selectedReportsData[0].turn_report}
                </div>
                <div>
                  <strong>{translate("Device")}:</strong>{" "}
                  {selectedReportsData[0].id_dispositif}
                </div>
              </div>

              <div style={{ display: "grid", gap: 12 }}>
                {selectedReportsData.map((report: ReportData) => (
                  <div
                    key={report.id_report}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: 14,
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>
                          [{report.type_report}]{" "}
                          {getReportName(report.type_report, translate)}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          ID: {report.id_report}
                        </div>
                      </div>

                      <Badge
                        bg={report.state_report === 1 ? "success" : "secondary"}
                      >
                        {report.state_report === 1
                          ? translate("Open")
                          : translate("Unavailable")}
                      </Badge>
                    </div>

                    {report.state_report === 1 ? (
                      <Button
                        variant="outline-primary"
                        onClick={() => handleItemClick(report)}
                      >
                        <i className="las la-eye mr-2"></i>
                        {translate("View report")}
                      </Button>
                    ) : (
                      <Button variant="outline-secondary" disabled>
                        <i className="las la-ban mr-2"></i>
                        {translate("Unavailable")}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p>{translate("No data available for selected turn report.")}</p>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <Modal
        show={showConfirmationModal}
        onHide={handleCancelConfirmation}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{translate("Confirmation de suppression")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {translate("Êtes-vous sûr de vouloir supprimer les rapports sélectionnés ?")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelConfirmation}>
            {translate("Annuler")}
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmation}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner animation="border" size="sm" className="mr-2" />
                {translate("Deleting")}
              </>
            ) : (
              translate("Confirmer la suppression")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default FleetReports;